import { useState, useRef } from 'react';
import { DashboardTab } from '../../types/tabs.types';

interface UseTabRenameReturn {
  renamingTabId: string | null;
  renameValue: string;
  renameInputRef: React.RefObject<HTMLInputElement | null>;
  handleStartRename: (tabId: string, tabs: DashboardTab[]) => void;
  handleFinishRename: (onTabRename: (tabId: string, newName: string) => void) => void;
  handleCancelRename: () => void;
  handleRenameKeyDown: (
    event: React.KeyboardEvent,
    onTabRename: (tabId: string, newName: string) => void,
  ) => void;
  setRenameValue: (value: string) => void;
}

export const useTabRename = (): UseTabRenameReturn => {
  const [renamingTabId, setRenamingTabId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement | null>(null);

  const handleStartRename = (tabId: string, tabs: DashboardTab[]) => {
    const tab = tabs.find((t) => t.id === tabId);
    if (tab) {
      setRenameValue(tab.name);
      setRenamingTabId(tabId);

      // Focus the input after it renders
      setTimeout(() => {
        if (renameInputRef.current) {
          renameInputRef.current.focus();
          renameInputRef.current.select();
        }
      }, 0);
    }
  };

  const handleFinishRename = (onTabRename: (tabId: string, newName: string) => void) => {
    if (renamingTabId && renameValue.trim()) {
      onTabRename(renamingTabId, renameValue.trim());
    }
    setRenamingTabId(null);
    setRenameValue('');
  };

  const handleCancelRename = () => {
    setRenamingTabId(null);
    setRenameValue('');
  };

  const handleRenameKeyDown = (
    event: React.KeyboardEvent,
    onTabRename: (tabId: string, newName: string) => void,
  ) => {
    if (event.key === 'Enter') {
      handleFinishRename(onTabRename);
    } else if (event.key === 'Escape') {
      handleCancelRename();
    }
  };

  return {
    renamingTabId,
    renameValue,
    renameInputRef,
    handleStartRename,
    handleFinishRename,
    handleCancelRename,
    handleRenameKeyDown,
    setRenameValue,
  };
};
